import { EditingMode, PermissionRole } from '@aline/common';

/** What forces a document into viewing mode, and what the user may do in it otherwise. */
export interface EditingPermissions {
  /** True while a read-only state (signing ceremony, field walkthrough, lost connection) lasts. */
  forceViewing: boolean;
  permissionRole: PermissionRole | null;
}

export function editingModeEnabledForUser(
  editingMode: EditingMode,
  permissionRole: PermissionRole | null
): boolean {
  if (!permissionRole) return false;

  switch (permissionRole) {
    case PermissionRole.Owner:
    case PermissionRole.Editor:
      return true;
    case PermissionRole.Suggester:
      return editingMode !== EditingMode.EDITING;
    case PermissionRole.Commenter:
      return [EditingMode.COMMENTING, EditingMode.VIEWING].includes(editingMode);
    case PermissionRole.Viewer:
      return editingMode === EditingMode.VIEWING;
  }
}

export function mostPermissiveEditingMode(userRole: PermissionRole): EditingMode {
  switch (userRole) {
    case PermissionRole.Owner:
    case PermissionRole.Editor:
      return EditingMode.EDITING;
    case PermissionRole.Suggester:
      return EditingMode.SUGGESTING;
    case PermissionRole.Commenter:
      return EditingMode.COMMENTING;
    case PermissionRole.Viewer:
      return EditingMode.VIEWING;
  }
}

/**
 * The mode a document is in, given the user's permissions and the mode they picked for it.
 *
 * A read-only state wins for as long as it lasts. Otherwise the pick stands while the user's role
 * still allows it, so re-resolving with unchanged permissions never moves the user off the mode
 * they chose. With no pick, or a pick the role rules out, the role's most permissive mode applies.
 */
export function resolveEditingMode({
  permissions: { forceViewing, permissionRole },
  preferredEditingMode,
}: {
  permissions: EditingPermissions;
  preferredEditingMode: EditingMode | null;
}): EditingMode {
  if (forceViewing || !permissionRole) return EditingMode.VIEWING;
  if (preferredEditingMode && editingModeEnabledForUser(preferredEditingMode, permissionRole))
    return preferredEditingMode;
  return mostPermissiveEditingMode(permissionRole);
}
